/* eslint-disable no-nested-ternary */
import LoadingAnimation from 'components/LoadingAnimation';
import SubmitallViewerModal from 'components/modals/SubmittalViewerModal';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import Fuse from 'fuse.js';
import Immutable, { OrderedMap } from 'immutable';
import React, { CSSProperties, useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { Doc } from 'records/Doc';
import { ModalState } from 'records/ModalState';
import { Page } from 'records/Page';
import { Submittal, SubmittalOrderedMap } from 'records/Submittal';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';
import { ApprovedFileSortFields } from '../SortButton';
import ApprovedFileRenderer from './ApprovedFileRenderer';

interface SubWithDoc {
  sub: Submittal;
  doc: Doc;
  firstPage: Page;
}

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: Immutable.OrderedMap<string, SubWithDoc>;
}

interface StyleProps {
  height: number;
  width: number;
}

export type SubWithDocMap = Immutable.OrderedMap<string, SubWithDoc>;

const searchOptions: Fuse.IFuseOptions<SubWithDoc> = {
  keys: ['doc.filename'],
};

interface Props extends WithAppStoreProps {
  specNumber: string | null;
  subsBySpecNumber: Immutable.OrderedMap<string, SubmittalOrderedMap>;
  search: string;
  sortDirection: 'ascending' | 'descending';
  sortField: ApprovedFileSortFields;
  filesAreDownloaded: boolean;
}

const ApprovedFiles = (props: Props) => {
  const { actions } = props;
  const [pagesLoaded, setPagesLoaded] = useState<boolean>(false);
  const { translate } = useTranslator(['submittalsPage']);

  useEffect(() => {
    actions.setSelectedApprovedFiles(Immutable.Set<string>());
    init();
    return () => {
      actions.setSelectedApprovedFiles(Immutable.Set<string>());
    };
  }, []);

  const init = async (): Promise<void> => {
    const { subsBySpecNumber, specNumber } = props;

    const subs = subsBySpecNumber.get(
      specNumber || '',
      Immutable.OrderedMap() as SubmittalOrderedMap
    );

    const docIdsArr = subs
      .valueSeq()
      .flatMap((s) => s.attachments)
      .toArray();

    await Promise.all(docIdsArr.map(actions.getFirstPage));

    setPagesLoaded(true);
  };

  const handleSelectAll = (checked: boolean): void => {
    const { specNumber, subsBySpecNumber, appState } = props;
    const { documents } = appState;

    if (checked) {
      const subs = subsBySpecNumber.get(
        specNumber || '',
        Immutable.OrderedMap() as SubmittalOrderedMap
      );

      const docIds = subs.valueSeq().flatMap((sub) => {
        return documents
          .filter((d) => sub.attachments.includes(d.documentid))
          .map((doc) => doc.documentid)
          .valueSeq()
          .toList();
      });

      // setState({ selectedFiles: new Set(docIds) });
      actions.setSelectedApprovedFiles(Immutable.Set<string>(docIds));
    } else {
      // setState({ selectedFiles: new Set() });
      actions.setSelectedApprovedFiles(Immutable.Set<string>());
    }
  };

  const handleDocThumbnailClick = (doc: Doc, submittal: Submittal): void => {
    const modalState = new ModalState({
      id: 'SubmittalViewer',
      portal: 'main',
      open: true,
      component: SubmitallViewerModal,
      props: {
        doc,
        submittalId: submittal.id,
        showToolbar: false,
      },
    });

    actions.createModal(modalState);
  };

  const handleDocCheckboxChange = (checked: boolean, docId: string) => {
    const { addSelectedApprovedFile, deleteSelectedApprovedFile } = actions;

    if (checked) {
      addSelectedApprovedFile(docId);
    } else {
      deleteSelectedApprovedFile(docId);
    }
  };

  const sortByCategory = (docsWithSub: SubWithDocMap): SubWithDocMap => {
    const { sortDirection } = props;
    const sortedDocs = docsWithSub.sort((subA, subB): number => {
      const a =
        sortDirection === 'ascending'
          ? subA.sub.category.display
          : subB.sub.category.display;
      const b =
        sortDirection === 'ascending'
          ? subB.sub.category.display
          : subA.sub.category.display;

      return a < b ? -1 : a > b ? 1 : 0;
    });
    return sortedDocs;
  };

  const sortByDateApproved = (docsWithSub: SubWithDocMap): SubWithDocMap => {
    const { sortDirection } = props;
    const sortedDocs = docsWithSub.sort((subA, subB): number => {
      const a =
        sortDirection === 'ascending'
          ? subA.sub.subrespondedon
          : subB.sub.subrespondedon;
      const b =
        sortDirection === 'ascending'
          ? subB.sub.subrespondedon
          : subA.sub.subrespondedon;
      return a < b ? -1 : a > b ? 1 : 0;
    });
    return sortedDocs;
  };

  const sortByFirstName = (docsWithSub: SubWithDocMap): SubWithDocMap => {
    const { sortDirection } = props;
    const sortedDocs = docsWithSub.sort((subA, subB): number => {
      const a =
        sortDirection === 'ascending'
          ? subA.sub.reviewedby
          : subB.sub.reviewedby;
      const b =
        sortDirection === 'ascending'
          ? subB.sub.reviewedby
          : subA.sub.reviewedby;
      return a < b ? -1 : a > b ? 1 : 0;
    });
    return sortedDocs;
  };

  const sortByReview = (docsWithSub: SubWithDocMap): SubWithDocMap => {
    const { sortDirection } = props;
    const sortedDocs = docsWithSub.sort((subA, subB): number => {
      const a =
        sortDirection === 'ascending'
          ? subA.sub.reviewerresponse
          : subB.sub.reviewerresponse;
      const b =
        sortDirection === 'ascending'
          ? subB.sub.reviewerresponse
          : subA.sub.reviewerresponse;
      return a < b ? -1 : a > b ? 1 : 0;
    });
    return sortedDocs;
  };

  const {
    specNumber,
    subsBySpecNumber,
    search,
    appState,
    sortField,
    filesAreDownloaded,
  } = props;
  const { documents, selectedApprovedFiles } = appState;

  const subs = subsBySpecNumber.get(
    specNumber || '',
    Immutable.OrderedMap() as SubmittalOrderedMap
  );

  let docsWithSub = subs
    .valueSeq()
    .flatMap((sub) => {
      const subDocs = documents
        .filter((d) => sub.attachments.includes(d.documentid))
        .valueSeq()
        .toList()
        .map((doc) => {
          const firstPage = appState.pages
            .valueSeq()
            .filter((p) => p.documentid === doc.documentid)
            .sortBy((p) => p.pageIndex)
            .first<Page>(new Page());

          return { sub, doc, firstPage };
        });

      return subDocs;
    })
    .toArray();

  if (search.length > 0) {
    const fuse = new Fuse(docsWithSub, searchOptions);

    docsWithSub = fuse.search(search).map((rr) => rr.item);
  }
  let docs: SubWithDocMap = OrderedMap<string, SubWithDoc>();
  docs = docsWithSub.reduce((map, d) => {
    return map.set(d.doc.documentid, d);
  }, docs);

  switch (sortField) {
    case 'dateapproved':
      docs = sortByDateApproved(docs);
      break;
    case 'firstname':
      docs = sortByFirstName(docs);
      break;
    case 'reviewerresponse':
      docs = sortByReview(docs);
      break;
    case 'category':
    default:
      docs = sortByCategory(docs);
      break;
  }

  const itemHeight = 112;

  return (
    <StdList gridTemplateColumns="48px 1fr" id="approved-sub-files-list">
      <StdListHeader>
        <StdListHeaderCheckbox
          id="approved-files-select-all-checkbox"
          checked={
            docsWithSub.length > 0 &&
            selectedApprovedFiles.size >= docsWithSub.length
          }
          indeterminate={selectedApprovedFiles.size > 0}
          onChange={handleSelectAll}
        />
        <StdListHeaderCell text={translate('submittalsPage:approvedFiles')} />
      </StdListHeader>
      <StdListBody enableScrolling={false}>
        {pagesLoaded && filesAreDownloaded ? (
          <AutoSizer>
            {({ height, width }: StyleProps) => (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={docs.size}
                itemSize={itemHeight}
                itemData={docs}
              >
                {({ style, index, data }: RenderingProps) =>
                  ApprovedFileRenderer(
                    style,
                    index,
                    data,
                    selectedApprovedFiles,
                    handleDocThumbnailClick,
                    handleDocCheckboxChange
                  )
                }
              </FixedSizeList>
            )}
          </AutoSizer>
        ) : (
          <div
            className="flex-center"
            style={{
              height: 'calc(100% - 48px)',
            }}
          >
            <div className="column items-center">
              <LoadingAnimation
                pageName={translate('submittalsPage:loadingSubmittals')}
              />
            </div>
          </div>
        )}
      </StdListBody>
    </StdList>
  );
};

export default withAppStore<Props>(ApprovedFiles);
